import React, { Fragment, ReactNode, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

interface EntityScreenProps {
  /** Nombre del parámetro de la URL que identifica la entidad (por defecto `id`). */
  param?: string;
  /**
   * Clave de la entidad cuando no viene en la URL (p. ej. se lee de sessionStorage).
   * Se evalúa UNA vez por navegación (cada `location.key`), no en cada render.
   */
  entityKey?: () => string | null;
  children: ReactNode;
}

/**
 * Monta de cero la pantalla de un asistente cuando cambia la entidad.
 *
 * React Router reutiliza el elemento de la ruta cuando solo cambia el `:id`
 * (atrás/adelante entre dos entradas de la misma pantalla), así que los
 * `defaultValues` de react-hook-form y los efectos que solo corren al montar
 * se quedan con la entidad ANTERIOR. Usar la entidad como `key` fuerza el
 * remontaje (R3/R4 del spec wizard-draft-ownership).
 */
export const EntityScreen = ({ param = 'id', entityKey, children }: EntityScreenProps) => {
  const params = useParams();
  // Cada navegación (incluso a la misma URL) trae un `location.key` nuevo.
  const { key: navigationKey } = useLocation();
  const paramValue = params[param];

  // 🔴 La clave se calcula una vez por NAVEGACIÓN (Ruling 19). Leer
  // `entityKey()` en cada render remontaba el asistente sin navegar: p. ej.
  // `phase2Queue` borra `new-action-data` antes de redirigir, y un re-render
  // intermedio cambiaba la `key` y montaba la pantalla de cero a mitad del
  // guardado. Solo una navegación nueva (o un `:id` nuevo) puede remontar.
  const key = useMemo(
    () => (entityKey ? entityKey() : paramValue),
    // `navigationKey` no se lee dentro: es el disparador (una vez por navegación).
    [navigationKey, paramValue, entityKey]
  );

  return <Fragment key={key ?? undefined}>{children}</Fragment>;
};
